import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from '../utils/axios';
import toast from 'react-hot-toast';
import { Eye, Download, Search, Edit, Plus, FileText, Users, BarChart3, Settings, Calculator, Receipt } from 'lucide-react';
import { generatePDF } from '../utils/pdfGenerator';

const InvoiceHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState({
    payment_type: 'cash',
    payment_status: 'pending',
    amount_received: 0
  });
  const [monthlyStats, setMonthlyStats] = useState({ total: 0, amount: 0 });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPreviousPage: false
  });

  useEffect(() => {
    fetchInvoices();
  }, [location]); // Add location dependency to refresh when navigating back

  // Effect for debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchInvoices(0, 1); // Reset to page 1 on search
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchInvoices = async (retryCount = 0, page = 1) => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page,
        limit: 10
      });

      if (searchTerm.trim()) {
        queryParams.append('search', searchTerm.trim());
      }

      const response = await axios.get(`/sales/invoices?${queryParams.toString()}`);
      setInvoices(response.data.invoices);
      setFilteredInvoices(response.data.invoices); // Backend handles filtering
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to load invoices:', error);
      
      // Handle 429 (Too Many Requests) with retry logic
      if (error.response?.status === 429 && retryCount < 2) {
        console.log(`Rate limited, retrying in ${(retryCount + 1) * 2} seconds...`);
        setTimeout(() => {
          fetchInvoices(retryCount + 1, page);
        }, (retryCount + 1) * 2000); // Wait 2s, then 4s
        return;
      }
      
      // Use sample data with payment status when API fails
      console.log('API unavailable, using sample data');
      const sampleInvoices = [
        {
          id: 'INV-001',
          customer_name: 'ABC Company',
          total_amount: 25000,
          date: '2025-11-10',
          payment_status: 'paid',
          payment_type: 'cash',
          amount_received: 25000
        },
        {
          id: 'INV-002',
          customer_name: 'XYZ Corp',
          total_amount: 18000,
          date: '2025-11-09',
          payment_status: 'partial',
          payment_type: 'card',
          amount_received: 10000
        },
        {
          id: 'INV-003',
          customer_name: 'Tech Solutions',
          total_amount: 32000,
          date: '2025-11-08',
          payment_status: 'pending',
          payment_type: 'upi',
          amount_received: 0
        }
      ];
      setInvoices(sampleInvoices);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalItems: sampleInvoices.length,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPreviousPage: false
      });
      
      if (error.response?.status === 429) {
        toast.error('Server is busy. Please try again later.');
      } else {
        toast('Unable to load invoices. Please check your connection.', { icon: '⚠️' });
      }
    } finally {
      setLoading(false);
    }
  };

  const filterInvoices = () => {
    let filtered = invoices;

    if (searchTerm) {
      filtered = filtered.filter(inv =>
        inv.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredInvoices(filtered);
  };

  const calculateMonthlyStats = () => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    const monthlyInvoices = invoices.filter(inv => {
      const invDate = new Date(inv.date);
      return invDate.getMonth() === currentMonth && invDate.getFullYear() === currentYear;
    });
    
    const total = monthlyInvoices.length;
    const amount = monthlyInvoices.reduce((sum, inv) => sum + inv.total_amount, 0);
    
    setMonthlyStats({ total, amount });
  };

  const handleViewDetails = async (invoiceId) => {
    try {
      const response = await axios.get(`/sales/invoices/${invoiceId}`);
      setSelectedInvoice(response.data);
      setShowDetails(true);
    } catch (error) {
      console.error('Failed to load invoice details:', error);
      
      // Fallback: use data from the list
      const invoice = invoices.find(inv => inv.id === invoiceId);
      if (invoice) {
        setSelectedInvoice(invoice);
        setShowDetails(true);
      } else {
        toast.error('Failed to load invoice details');
      }
    }
  };

  const handleUpdatePayment = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentData({
      payment_type: invoice.payment_type || 'cash',
      payment_status: invoice.payment_status || 'pending',
      amount_received: invoice.amount_received || 0
    });
    setShowPaymentModal(true);
  };

  const handleSavePayment = async () => {
    try {
      const updatedInvoice = {
        ...selectedInvoice,
        ...paymentData,
        payment_status: paymentData.amount_received >= selectedInvoice.total_amount ? 'paid' : 
                       paymentData.amount_received > 0 ? 'partial' : 'pending'
      };

      // Update local state
      setInvoices(prev => prev.map(inv => 
        inv.id === selectedInvoice.id ? updatedInvoice : inv
      ));

      toast.success('Payment details updated successfully!');
      setShowPaymentModal(false);
      setSelectedInvoice(null);
    } catch (error) {
      toast.error('Failed to update payment details');
    }
  };

  const handleDownloadPDF = async (invoiceId) => {
    try {
      let invoiceData;
      
      // Try to get detailed invoice data from API
      try {
        const response = await axios.get(`/sales/invoices/${invoiceId}`);
        invoiceData = response.data;
      } catch (apiError) {
        console.log('API call failed, using local invoice data');
        // Fallback to local data
        const localInvoice = invoices.find(inv => inv.id === invoiceId);
        if (!localInvoice) {
          throw new Error('Invoice not found');
        }
        
        // Create a proper invoice object for PDF generation
        invoiceData = {
          id: localInvoice.id,
          customer_name: localInvoice.customer_name,
          customer_phone: localInvoice.customer_phone || '',
          customer_email: localInvoice.customer_email || '',
          customer_address: localInvoice.customer_address || '',
          total_amount: localInvoice.total_amount,
          date: localInvoice.date,
          items: localInvoice.items || [
            {
              product_name: 'Invoice Item',
              quantity: 1,
              unit_price: localInvoice.total_amount,
              subtotal: localInvoice.total_amount
            }
          ],
          employee: {
            name: 'Sales Person'
          }
        };
        
        if (apiError.response?.status === 429) {
          toast('Server is busy, generating PDF with available data', { icon: '⚠️' });
        }
      }
      
      // Generate PDF with the invoice data
      generatePDF(invoiceData, 'invoice');
      toast.success('PDF generated successfully');
      
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF: ' + (error.message || 'Unknown error'));
    }
  };

  const handleEditInvoice = (invoiceId) => {
    navigate(`/sales/invoice/edit/${invoiceId}`);
  };


  const handlePageChange = (page) => {
    setLoading(true);
    fetchInvoices(0, page);
  };

  if (loading) {
    return (
      <div className="flex-1 p-8">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      <div className="flex-1">
        {/* Header */}
        <div className="bg-white border-b border-slate-100 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Invoices</h1>
              <p className="text-gray-600">Manage your sales invoices and payments</p>
            </div>
            <button
              onClick={() => navigate('/sales/invoice/new')}
              className="bg-[#C5A57D] hover:bg-[#B8985E] text-white px-4 py-2 rounded-lg flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>New Invoice</span>
            </button>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search invoices..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A57D] focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-600">
                {pagination && pagination.totalItems > 0 
                  ? `Showing ${((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to ${Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of ${pagination.totalItems} invoices`
                  : '0 invoices'
                }
              </span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-6">

          {/* Monthly Section */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Nov 2025</h2>
              {pagination && pagination.totalItems > 0 && (
                <div className="text-sm text-gray-600">
                  {filteredInvoices.length} of {pagination.totalItems} invoices shown
                </div>
              )}
            </div>
            
            {filteredInvoices.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <div className="text-lg font-medium text-slate-900 mb-2">No invoices found</div>
                <div className="text-sm text-gray-500 mb-4">
                  {invoices.length === 0 
                    ? "Create your first invoice to start tracking sales."
                    : "No invoices match your current search criteria."
                  }
                </div>
                <button
                  onClick={() => navigate('/sales/invoice/new')}
                  className="btn-primary"
                >
                  Create First Invoice
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredInvoices.map((invoice) => (
                  <div key={invoice.id} className="flex items-center justify-between p-4 bg-white rounded-lg border border-slate-100 hover:shadow-sm transition-shadow">
                    <div className="flex items-center space-x-4 flex-1">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText className="h-5 w-5 text-[#0A5F38]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-slate-900 truncate">{invoice.customer_name}</div>
                        <div className="text-sm text-gray-500">{invoice.id}</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right min-w-0">
                        <div className="font-medium text-slate-900">₹{invoice.total_amount.toLocaleString()}</div>
                        <div className="text-sm text-gray-500">{new Date(invoice.date).toLocaleDateString()}</div>
                        <div className="text-xs mt-1">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            (invoice.payment_status || 'pending') === 'paid' 
                              ? 'bg-green-100 text-green-800'
                              : (invoice.payment_status || 'pending') === 'partial'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {(invoice.payment_status || 'pending') === 'paid' ? 'Paid' : 
                             (invoice.payment_status || 'pending') === 'partial' ? 'Partial' : 'Pending'}
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-2 flex-shrink-0">
                        <button
                          onClick={() => handleViewDetails(invoice.id)}
                          className="p-2 text-gray-400 hover:text-gray-600"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleUpdatePayment(invoice)}
                          className="p-2 text-blue-400 hover:text-[#0A5F38]"
                          title="Update Payment"
                        >
                          <Receipt className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDownloadPDF(invoice.id)}
                          className="p-2 text-gray-400 hover:text-gray-600"
                          title="Download PDF"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                
                <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-slate-100">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                      <span className="font-bold text-gray-600">{monthlyStats.total}</span>
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">Total</div>
                      <div className="text-sm text-gray-500">Overdue</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2 mt-6">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPreviousPage}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                <div className="flex space-x-1">
                  {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-2 text-sm border rounded-lg ${
                        page === pagination.currentPage
                          ? 'bg-[#C5A57D] text-white border-blue-600'
                          : 'border-gray-300 hover:bg-white'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNextPage}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Invoice Details Modal */}
        {showDetails && selectedInvoice && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-screen overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-slate-900">
                    Invoice Details #{selectedInvoice.id?.slice(-8) || 'N/A'}
                  </h2>
                  <button
                    onClick={() => setShowDetails(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Employee</h3>
                    <p>{selectedInvoice.employee?.name}</p>
                    <p className="text-sm text-gray-600">{selectedInvoice.employee?.phone}</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Customer</h3>
                    {selectedInvoice.customer ? (
                      <>
                        <p>{selectedInvoice.customer.name}</p>
                        <p className="text-sm text-gray-600">{selectedInvoice.customer.phone}</p>
                        <p className="text-sm text-gray-600">{selectedInvoice.customer.email}</p>
                      </>
                    ) : (
                      <p className="text-gray-500">Walk-in Customer</p>
                    )}
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Items</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-white">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tax</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedInvoice.items?.map((item, index) => (
                          <tr key={index}>
                            <td className="px-4 py-2 text-sm">{item.product_name}</td>
                            <td className="px-4 py-2 text-sm">{item.quantity}</td>
                            <td className="px-4 py-2 text-sm">₹{item.unit_price}</td>
                            <td className="px-4 py-2 text-sm">{item.tax}%</td>
                            <td className="px-4 py-2 text-sm">{item.discount}%</td>
                            <td className="px-4 py-2 text-sm font-semibold">₹{item.subtotal?.toFixed(1)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="text-right">
                    <div className="text-2xl font-bold text-slate-900">
                      Total: ₹{selectedInvoice.total_amount?.toFixed(1) || '0.00'}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      Status: <span className={`font-semibold ${
                        selectedInvoice.status === 'paid' ? 'text-green-600' :
                        selectedInvoice.status === 'pending' ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {selectedInvoice.status === 'paid' ? 'Paid' : 
                         selectedInvoice.status === 'pending' ? 'Pending' : 'Cancelled'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-4 mt-6">
                  <button
                    onClick={() => setShowDetails(false)}
                    className="btn-secondary"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => handleDownloadPDF(selectedInvoice.id)}
                    className="btn-primary"
                  >
                    Download PDF
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payment Update Modal */}
        {showPaymentModal && selectedInvoice && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <Receipt className="h-5 w-5 text-[#0A5F38]" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">Update Payment Details</h3>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Invoice: {selectedInvoice.id}
                    </label>
                    <div className="text-sm text-gray-600">
                      Total Amount: ₹{selectedInvoice.total_amount?.toLocaleString()}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Type
                    </label>
                    <select
                      value={paymentData.payment_type}
                      onChange={(e) => setPaymentData(prev => ({...prev, payment_type: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A57D] focus:border-transparent"
                    >
                      <option value="cash">Cash</option>
                      <option value="card">Card</option>
                      <option value="upi">UPI</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="cheque">Cheque</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Amount Received
                    </label>
                    <input
                      type="number"
                      value={paymentData.amount_received}
                      onChange={(e) => setPaymentData(prev => ({...prev, amount_received: parseFloat(e.target.value) || 0}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A57D] focus:border-transparent"
                      placeholder="Enter amount received"
                      min="0"
                      max={selectedInvoice.total_amount}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Status
                    </label>
                    <div className="text-sm text-gray-600">
                      {paymentData.amount_received >= selectedInvoice.total_amount ? 'Paid (Full)' : 
                       paymentData.amount_received > 0 ? 'Partial Payment' : 'Pending'}
                    </div>
                  </div>
                </div>
                
                <div className="flex space-x-3 mt-6">
                  <button
                    onClick={() => setShowPaymentModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSavePayment}
                    className="flex-1 px-4 py-2 bg-[#C5A57D] text-white rounded-lg hover:bg-[#B8985E] transition-colors"
                  >
                    Update Payment
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceHistory;



